import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import StackGrid from 'react-stack-grid'
import Media from 'react-media'
import decorator from '../../../core/decorator'
import breakpoints from '../../../styles/variables/breakpoints'

import ResultCard from '../../../components/result-card/result-card'
import Icon from '../../../core/patterns/icon/icon'
import GridIcon from '../../../resources/svgs/Grid.svg'
import ListIcon from '../../../resources/svgs/List.svg'
import Arrow from '../../../resources/svgs/Arrow.svg'

import { searchResultsSelector } from '../../../core/state/selectors/searchResults'
import actions from '../../../core/state/actions/searchResults'

import styles from './advanced-search-results.css'

const cx = classnames.bind(styles)

class ResultList extends React.PureComponent {
  static propTypes = {
    results: PropTypes.array,
    mode: PropTypes.oneOf(['tile', 'list']),
    setMode: PropTypes.func,
    isLoading: PropTypes.bool,
    total: PropTypes.number,
    loadMore: PropTypes.bool,
    i18n: PropTypes.func,
    onLoadMore: PropTypes.func,
    userIsLoggedIn: PropTypes.bool,
    dataRequested: PropTypes.bool,
    searchTerm: PropTypes.string,
    loadingMore: PropTypes.bool,
  }

  _setViewMode = ({ currentTarget }) => {
    const mode = currentTarget.dataset.mode
    this.props.setMode(mode)
  }

  _renderLoadMoreButton = () => {
    const {
      loadMore,
      onLoadMore,
      i18n,
      dataRequested,
      loadingMore,
    } = this.props

    if (dataRequested && !loadMore) return null

    const dotsClassName = cx({
      loadmore__dots: true,
      'loadmore__dots--hidden': !loadingMore,
    })

    return (
      <button onClick={onLoadMore} className={styles['loadmore']} type="button">
        <div className={dotsClassName}>
          <span className={styles['loadmore__dots__dot']} />
          <span className={styles['loadmore__dots__dot']} />
          <span className={styles['loadmore__dots__dot']} />
        </div>
        {i18n('AdvancedSearch.LoadMore')}
        <Icon className={styles['loadmore__icon']} glyph={Arrow} />
      </button>
    )
  }

  _renderHeader = () => {
    const { i18n, isLoading, total, mode } = this.props
    if (isLoading === false && total === 0) return null

    const getIconClassName = type =>
      cx({
        header__icon: true,
        'header__icon--selected': type === mode,
      })

    return (
      <header className={styles['header']}>
        <h2 className={styles['header__title']}>
          {total} {i18n('AdvancedSearch.Results')}
        </h2>
        <div>
          <button
            onClick={this._setViewMode}
            data-mode="tile"
            type="button"
            className={styles['header__button']}
          >
            <Icon glyph={GridIcon} className={getIconClassName('tile')} />
          </button>
          <button
            onClick={this._setViewMode}
            data-mode="list"
            type="button"
            className={styles['header__button']}
          >
            <Icon glyph={ListIcon} className={getIconClassName('list')} />
          </button>
        </div>
      </header>
    )
  }

  _renderNoResultsDescription = () => {
    const { i18n, total, searchTerm, results } = this.props

    if (results.length === 0 && total !== null) {
      return (
        <React.Fragment>
          <h2 className={styles['noresults__title']}>
            {i18n('WeCouldNotFindAnyResultsFor') + ` "${searchTerm}"`}
          </h2>
          <p>{i18n('SearchResults.NoResultsTips')}</p>
          <ul className={styles['noresults__list']}>
            <li>{i18n('SearchResults.NoResultsTip1')}</li>
            <li>{i18n('SearchResults.NoResultsTip2')}</li>
          </ul>
        </React.Fragment>
      )
    }

    return null
  }

  render() {
    const {
      results = [],
      mode,
      i18n,
      userIsLoggedIn,
      total,
      isLoading,
    } = this.props

    return (
      <section className={styles['wrapper']} key={mode}>
        {this._renderHeader()}
        {this._renderNoResultsDescription()}
        <Media query={{ minWidth: breakpoints.s }}>
          {matches => {
            if (isLoading === false && total === 0) return null

            return (
              <StackGrid
                columnWidth={matches && mode === 'tile' ? '50%' : '100%'}
                gutterWidth={14}
                gutterHeight={14}
                duration={800}
                className={styles['results']}
              >
                {results.map(result => {
                  return (
                    <ResultCard
                      key={result.id}
                      id={result.id}
                      title={result.name}
                      type={result.type}
                      image={result.image}
                      url={result.url}
                      mode={mode}
                      userIsLoggedIn={userIsLoggedIn}
                      isFavourited={result.favourited}
                      buttonText={i18n('AdvancedSearch.ButtonCta')}
                      list={result.events}
                      infoText={result.eventCountText}
                    />
                  )
                })}
              </StackGrid>
            )
          }}
        </Media>

        {this._renderLoadMoreButton()}
      </section>
    )
  }
}

const connectState = state => {
  return {
    results: searchResultsSelector(state),
    mode: state.searchResults.mode,
    isLoading: state.searchResults.loading,
    loadingMore: state.searchResults.loadingMore,
    total: state.searchResults.total,
    limit: state.searchResults.limit,
    userIsLoggedIn: Boolean(state.user),
    dataRequested: state.searchResults.dataRequested,
    loadMore: state.searchResults.results.length < state.searchResults.total,
  }
}

const connectDispatch = {
  setMode: actions.setMode,
}

export default decorator({ connectState, connectDispatch, translatable: true })(
  ResultList
)
